import { useMutation, useQuery } from '@tanstack/react-query';
import { List, ListItem, Toggle } from 'framework7-react';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import { useEntertainerEngagementContext } from '@contexts/EntertainerEngagementContext';
import dayjs from 'dayjs';
import { uniq } from 'lodash';
import pluralize from 'pluralize';

import { useCreateInstagramAccountMutation } from '@admin/mutations/InstagramAccountsMutations';

import API from '@api/API';

import { Entertainer, useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import { useFeaturedLooksFeedPostsQuery } from '@queries/PostQueries';
import { useSeriesByTagSlug, useSeriesEntertainersQuery } from '@queries/TagQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCitiesQuery, useCityById } from '@hooks/useCurrentUserLocation';

import { EntertainerDashboardAction, useLocation, useNavigate, useParams } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import EventCard, {
  EventActions,
  EventHeader,
  EventImage,
  PromoterHeader,
} from '@components/cards/EventCard';
import { VenueCard } from '@components/cards/VenueCard';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import { ButtonFilter } from '@components/layout/ButtonFilter/ButtonFilter';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { ErrorBoundary } from '@components/layout/ErrorBoundary';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LinkBanner } from '@components/layout/LinkBanner/LinkBanner';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen/Screen';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { UpdateEntertainerList } from '@screens/series/SeriesScreen/SeriesScreen';
import {
  SweepstakesCard,
  useSweepstakesReferralLink,
} from '@screens/series/SeriesScreen/SweepstakesCard';

import { FeaturedKing } from '../EntertainerDetailsScreen/assets/FeaturedKing';
import { LandingPageSweepstakesCard } from '../EntertainerSweepstakesScreen/EntertainerSweepstakesScreen';
import { FeaturedLook, useFeaturedLooksQuery } from '../FeaturedLooksScreen/FeaturedLooksScreen';
import { MultiSelectField } from '../components/CitiesField';
import {
  AddFeaturedPhotoPopup,
  AddImageContent,
} from '../components/FeaturedPhotoManagementSection';
import { ScheduledSocialPostSection } from './ScheduledSocialPost';
import tickets from './tickets.svg';

interface DragKingCommunityScreenProps {}

interface DragKingCommunitySubmission {
  _id: string;
  title: string;
  image?: string;
  status: string;
  entertainer?: {
    _id: string;
    title: string;
    bookingHandle: string;
  };
}

interface ScheduledSocialPostEntertainers {
  entertainers: Entertainer[];
}

const useScheduledSocialPosts = (currentUserId: string) => {
  const { data, isFetched } = useQuery({
    queryKey: ['scheduledSocialPosts', currentUserId],
    queryFn: () => API.get<{ scheduledSocialPosts: any[] }>(`/v1/scheduled-social-posts`),
    enabled: !!currentUserId,
  });

  return {
    scheduledSocialPosts: data?.scheduledSocialPosts,
    areScheduledSocialPostsReady: isFetched,
  };
};

const useScheduledSocialPostEntertainersByCityId = (cityId: string) => {
  const { data, isFetched } = useQuery({
    queryKey: ['scheduledSocialPostEntertainers', cityId],
    queryFn: () =>
      API.get<ScheduledSocialPostEntertainers>(
        `/v1/cities/${cityId}/event-social-post-entertainers`,
      ),
    enabled: !!cityId,
  });

  return {
    scheduledSocialPostEntertainers: data?.entertainers,
    areScheduledSocialPostEntertainersReady: isFetched,
  };
};

const useUpdateLastCheckedAt = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ entertainerId }: { entertainerId: string }) =>
      API.post(`/v1/entertainers/${entertainerId}/checked-at`, {}),
  });

  return {
    isUpdatingLastCheckedAt: isPending,
    updateLastCheckedAtAsync: mutateAsync,
  };
};

const EntertainerCard: FC<{
  entertainer: Entertainer;
}> = ({ entertainer }) => {
  const [lastCheckedAt, setLastCheckedAt] = useState(entertainer.lastCheckedAt);
  const { updateLastCheckedAtAsync } = useUpdateLastCheckedAt();

  const image = entertainer.avatar || entertainer.featuredMedia?.[0]?.image;
  const imageSize = 3 * 18;

  const handleViewInstagram = () => {
    openExternalUrl(`https://instagram.com/${entertainer?.contact?.instagram ?? ''}`);
  };

  const handleUpdateLastCheckedAt = async () => {
    setLastCheckedAt(new Date().toISOString());
    await updateLastCheckedAtAsync({
      entertainerId: entertainer._id,
    });
  };

  return (
    <div>
      <Card>
        <div>
          <Card.Header
            className="p-2"
            avatar={
              <div
                style={{
                  position: 'relative',
                  width: imageSize,
                  height: imageSize,
                }}
              >
                <div style={{ borderRadius: 8, overflow: 'hidden', width: '100%', height: '100%' }}>
                  {image ? (
                    <AsyncImage
                      src={image}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      placeholder={
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            background: 'rgba(255,255,255,.2)',
                          }}
                        />
                      }
                    />
                  ) : (
                    <div
                      style={{ width: '100%', height: '100%', background: 'rgba(255,255,255,.2)' }}
                    />
                  )}
                </div>
              </div>
            }
            tagline={entertainer.cities.map((city) => city.name).join(' • ')}
            title={entertainer.title}
            subtitle={`Last checked: ${lastCheckedAt ? dayjs(lastCheckedAt).format('MM/DD/YYYY [at] HH:mm A') : 'Never'}`}
          />
          <Card.Footer className="d-flex gap-2 px-2 pb-2">
            <Button
              className="py-0"
              style={{ height: 36 }}
              variant="smooth"
              color="neutral"
              roundness="rounded"
              onClick={handleViewInstagram}
            >
              View Instagram
            </Button>
            <Button
              className="py-0"
              style={{ height: 36 }}
              variant="smooth"
              color="neutral"
              roundness="rounded"
              onClick={handleUpdateLastCheckedAt}
            >
              Update "Checked At"
            </Button>
          </Card.Footer>
        </div>
      </Card>
    </div>
  );
};

const AddEntertainerPopupContent: FC<{
  cityId: string;
  seriesId: string;
  onClose: () => void;
  entertainers: Entertainer[];
}> = ({ cityId, seriesId, onClose, entertainers }) => {
  const [tab, setTab] = useState('new');
  const [entertainerInstagram, setInstagram] = useState('');
  const [title, setTitle] = useState('');
  const [categories, setCategories] = useState<string[]>(['Drag King']);

  const { createInstagramAccountAsync } = useCreateInstagramAccountMutation();

  const handleSubmit = async () => {
    await createInstagramAccountAsync({
      title,
      username: entertainerInstagram,
      type: 'entertainer',
      cityId,
      categories,
      seriesId,
    });

    onClose();
  };

  return (
    <>
      <ButtonFilter
        selected={tab}
        options={[
          { value: 'new', label: 'New' },
          { value: 'existing', label: 'Existing' },
        ]}
        onChange={setTab}
      />

      {tab === 'new' && (
        <div className="d-grid gap-2">
          <Input
            placeholder="Instagram *"
            name="instagram"
            onChange={(event) => setInstagram(event.target.value)}
          />
          <Input
            placeholder="Stage Name"
            name="stageName"
            onChange={(event) => setTitle(event.target.value)}
          />
          <MultiSelectField
            name="categories"
            defaultValue={[]}
            placeholder="Categories"
            options={[
              { value: 'Drag King', label: 'Drag King' },
              { value: 'Drag Queen', label: 'Drag Queen' },
            ]}
            onChange={setCategories}
          />
          <div>
            <Button roundness="rounded" disabled={!entertainerInstagram} onClick={handleSubmit}>
              Add
            </Button>
          </div>
        </div>
      )}

      {tab === 'existing' && (
        <div className="d-grid gap-2">
          <UpdateEntertainerList entertainers={entertainers} seriesId={seriesId} cityId={cityId} />
        </div>
      )}
    </>
  );
};

const AddEntertainerPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  cityId: string;
  seriesId: string;
  entertainers: Entertainer[];
}> = ({ isVisible, onClose, cityId, seriesId, entertainers }) => {
  return (
    <Popup isVisible={isVisible} onClose={onClose}>
      <Popup.Content>
        <TitleToolbar text="Manange Entertainer List" size="sm" />
        {isVisible && (
          <AddEntertainerPopupContent
            cityId={cityId}
            seriesId={seriesId}
            entertainers={entertainers}
            onClose={onClose}
          />
        )}
      </Popup.Content>
    </Popup>
  );
};

const SHARE_OPTIONS = [
  { value: 'look', label: 'Add Look' },
  // { value: 'badge', label: 'Reward Badges' },
  { value: 'show', label: 'Add Show' },
];

const INCLUDED_CITIES = [
  // TODO: Add fetch to get the schedules
];

interface CityDragShowManagementScreenProps {}

export const CityDragShowManagementScreen = createScreenComponent<
  CityDragShowManagementScreenProps,
  { cityId?: string }
>(({ f7route }) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const [cityId, setCityId] = useState<string>(f7route.query?.cityId);
  const [didSubmitCity, setDidSubmitCity] = useState(!!f7route.query?.cityId);
  const { currentUser, refetchUser, currentUserIsReady } = useCurrentUser();
  const { cities = [] } = useCitiesQuery();
  const { city } = useCityById(cityId);
  const { createAuthGateHandler } = useAuthGate();
  const [addImageModeIsVisible, setAddImageModeIsVisible] = useState(false);
  const [addCustomSubmissionModeIsVisible, setAddCustomSubmissionModeIsVisible] = useState(false);
  const [lookOrShow, setLookOrShow] = useState('look');
  const [looksOrBadges, setLooksOrBadges] = useState('looks');
  const { openConfirmDialog } = useDialog();
  const { series } = useSeriesByTagSlug(city?.featuredSeries?.kingsThings);
  const { openGatedAddShowPopup } = useEntertainerEngagementContext();
  const { scheduledSocialPosts } = useScheduledSocialPosts(currentUser?._id);
  const {
    isFetched: seriesEntertainersAreReady,
    entertainers: seriesEntertainers = [],
    refetch: refetchSeriesEntertainers,
  } = useSeriesEntertainersQuery(series?._id, Boolean(series?._id));

  const currentSchedule = useMemo(() => {
    return scheduledSocialPosts?.find((schedule) => schedule.cityIds.includes(cityId));
  }, [cityId, scheduledSocialPosts]);

  const handleAddEvent = () => {
    openGatedAddShowPopup({
      cityId,
      isEntertainerOptional: true,
      seriesId: currentSchedule?.filterType === 'kings-only' ? series?._id : undefined,
    });
  };

  const filteredCities = useMemo(() => {
    const cityIds = uniq(scheduledSocialPosts?.flatMap((schedule) => schedule.cityIds) ?? []);

    return cities.filter((city) => cityIds.includes(city._id));
  }, [scheduledSocialPosts, cities]);

  return (
    <Screen
      name="City Drag Show Management Screen"
      hideToolbar
      headerProps={{
        title: <Screen.GagglTitle />,
        right: <ShareLinkButton url={`https://gaggl.app/drag-shoutouts`} />,
      }}
      bottomToolbar={
        <Screen.BottomToolbar maxWidth={425}>
          {city?._id && didSubmitCity && (
            <Button onClick={handleAddEvent} roundness="rounded">
              Add Flyer
            </Button>
          )}
        </Screen.BottomToolbar>
      }
    >
      <Screen.Content>
        <div className="d-flex mb-3 gap-3 align-items-center">
          <div>
            <strong className="fs-4">Local Drag Show Admin</strong>
            <br />
            <span>{`Manage shows for a city`}</span>
          </div>
        </div>

        {didSubmitCity ? (
          <>
            <Button
              roundness="rounded"
              variant="smooth"
              color="neutral"
              onClick={() => {
                setDidSubmitCity(false);
              }}
            >
              Change City
            </Button>

            <ScheduledSocialPostSection
              hideIndividualFeatureTitle
              cityId={cityId}
              description={`These are all the flyers that have been uploaded. Check if a show already exists and press "Add Flyer" to add a new one.`}
              castOverride={{
                text: 'Edit show',
                action: (eventId: string) => {
                  navigate(`/events/${eventId}/cleanup`, {
                    state: { back: `/noauth/manage/admin/shows?cityId=${cityId}` },
                  });
                },
              }}
            />

            <TitleToolbar
              text="Local Entertainers"
              size="sm"
              action={<span>{`${seriesEntertainers?.length ?? 0} total`}</span>}
            />

            <p className="mt-0">
              Below is the list of entertainers to review for this city. Click "View Instagram" to
              open their profile in another tab. After checking their profile for upcoming shows, be
              sure to update their "Checked At" date.
            </p>
            <p className="mb-1">What to look for:</p>
            <ul className="mt-1">
              <li>Feed: Look for any flyers or posts about upcoming shows.</li>
              <li>Tagged: Check if they’ve been tagged in any event flyers.</li>
              <li>Only add flyers that haven’t already been submitted.</li>
            </ul>

            <div className="mb-4">
              <Button
                roundness="rounded"
                variant="smooth"
                color="neutral"
                onClick={() => {
                  setAddCustomSubmissionModeIsVisible(true);
                }}
              >
                Manage Entertainer List
              </Button>
            </div>

            <div className="d-grid gap-3">
              {seriesEntertainers.map((entertainer) => (
                <EntertainerCard key={entertainer._id} entertainer={entertainer} />
              ))}
            </div>
          </>
        ) : (
          <>
            <p>To get started, select the city:</p>
            <div className="d-grid gap-2">
              <Select
                name="cityId"
                value={cityId}
                options={[
                  { value: '', label: '--' },
                  ...filteredCities.map(({ _id, name }) => ({
                    value: _id,
                    label: name,
                  })),
                ]}
                onChange={(e) => {
                  setCityId(e.target.value);
                }}
              />
              <Button
                roundness="rounded"
                disabled={!cityId && !!currentUser?._id}
                onClick={() => {
                  if (!currentUser?._id) {
                    createAuthGateHandler(() => {});
                    return;
                  }

                  track('Select City', {
                    cityName: city?.name,
                  });
                  setDidSubmitCity(true);
                  setAddImageModeIsVisible(true);
                }}
              >
                {!currentUser?._id ? 'Log in to continue' : 'Continue'}
              </Button>
            </div>
          </>
        )}
      </Screen.Content>
      <AddEntertainerPopup
        isVisible={addCustomSubmissionModeIsVisible}
        onClose={() => {
          refetchSeriesEntertainers();
          setAddCustomSubmissionModeIsVisible(false);
        }}
        cityId={cityId}
        seriesId={series?._id}
        entertainers={seriesEntertainers}
      />
    </Screen>
  );
});
